import {
  createDefaultStreamSettings,
  createStreamId,
  FIXED_STREAM_DEFAULTS,
  getCommittedSearchValue,
  isCustomStream,
  normalizeCustomStream,
  normalizeHashtagValue,
} from "./inbox-stream-settings"

/** API type ids (POST/PATCH `typeId`). */
export const INBOX_STREAM_TYPE = {
  FB_COMMENTS: 1,
  FB_MESSAGES: 2,
  FB_POSTS: 3,
  FB_RATINGS: 4,
  TW_MENTIONS: 5,
  TW_DMS: 6,
  TW_SALES: 7,
  TW_HOTEL_NAME: 8,
  TW_LOCATION: 9,
  IG_COMMENTS: 10,
  IG_HASHTAG: 11,
  IG_LOCATION: 12,
  IG_PHOTO_TAGS: 13,
  IG_MENTIONS: 14,
  IG_MESSAGES: 15,
  IG_MENTIONS_STORIES: 16,
  LI_MESSAGES: 17,
  LI_COMMENTS: 18,
  LI_MENTIONS: 19,
}

const FIXED_TYPE_ID_BY_PLATFORM = {
  facebook: {
    comments: INBOX_STREAM_TYPE.FB_COMMENTS,
    messages: INBOX_STREAM_TYPE.FB_MESSAGES,
    posts: INBOX_STREAM_TYPE.FB_POSTS,
    ratings: INBOX_STREAM_TYPE.FB_RATINGS,
  },
  twitter: {
    mentions: INBOX_STREAM_TYPE.TW_MENTIONS,
    direct_messages: INBOX_STREAM_TYPE.TW_DMS,
  },
  instagram: {
    comments: INBOX_STREAM_TYPE.IG_COMMENTS,
    photo_tags: INBOX_STREAM_TYPE.IG_PHOTO_TAGS,
    mentions: INBOX_STREAM_TYPE.IG_MENTIONS,
    direct_messages: INBOX_STREAM_TYPE.IG_MESSAGES,
    story_mentions: INBOX_STREAM_TYPE.IG_MENTIONS_STORIES,
  },
}

const TWITTER_RULE_TYPE_ID = {
  "Hotel Name": INBOX_STREAM_TYPE.TW_HOTEL_NAME,
  Location: INBOX_STREAM_TYPE.TW_LOCATION,
  Sales: INBOX_STREAM_TYPE.TW_SALES,
}

const CUSTOM_KIND_TYPE_ID = {
  hashtag: INBOX_STREAM_TYPE.IG_HASHTAG,
  instagram_location: INBOX_STREAM_TYPE.IG_LOCATION,
}

const TYPE_ID_PLATFORM = {
  [INBOX_STREAM_TYPE.FB_COMMENTS]: "facebook",
  [INBOX_STREAM_TYPE.FB_MESSAGES]: "facebook",
  [INBOX_STREAM_TYPE.FB_POSTS]: "facebook",
  [INBOX_STREAM_TYPE.FB_RATINGS]: "facebook",
  [INBOX_STREAM_TYPE.TW_MENTIONS]: "twitter",
  [INBOX_STREAM_TYPE.TW_DMS]: "twitter",
  [INBOX_STREAM_TYPE.TW_SALES]: "twitter",
  [INBOX_STREAM_TYPE.TW_HOTEL_NAME]: "twitter",
  [INBOX_STREAM_TYPE.TW_LOCATION]: "twitter",
  [INBOX_STREAM_TYPE.IG_COMMENTS]: "instagram",
  [INBOX_STREAM_TYPE.IG_HASHTAG]: "instagram",
  [INBOX_STREAM_TYPE.IG_LOCATION]: "instagram",
  [INBOX_STREAM_TYPE.IG_PHOTO_TAGS]: "instagram",
  [INBOX_STREAM_TYPE.IG_MENTIONS]: "instagram",
  [INBOX_STREAM_TYPE.IG_MESSAGES]: "instagram",
  [INBOX_STREAM_TYPE.IG_MENTIONS_STORIES]: "instagram",
}

const CUSTOM_STREAM_TYPE_IDS = new Set([
  INBOX_STREAM_TYPE.TW_SALES,
  INBOX_STREAM_TYPE.TW_HOTEL_NAME,
  INBOX_STREAM_TYPE.TW_LOCATION,
  INBOX_STREAM_TYPE.IG_HASHTAG,
  INBOX_STREAM_TYPE.IG_LOCATION,
])

export function isCustomStreamTypeId(typeId) {
  return CUSTOM_STREAM_TYPE_IDS.has(Number(typeId))
}

const FIXED_STREAM_ID_BY_TYPE = Object.fromEntries(
  Object.entries(FIXED_TYPE_ID_BY_PLATFORM).flatMap(([platformId, map]) =>
    Object.entries(map).map(([streamId, typeId]) => [`${platformId}:${typeId}`, streamId]),
  ),
)

export function normalizeInboxStreamRows(payload, clientId) {
  const raw = extractInboxStreamArray(payload)
  if (raw.length === 0) return []

  if (raw.every((row) => row?.typeId != null && !row?.streamType && !row?.subscriptions)) {
    return raw
  }

  const hasNestedShape = raw.some(
    (item) => item?.streamType && typeof item.streamType === "object",
  )
  if (hasNestedShape) {
    return normalizeInboxStreamFetchResponse(raw, clientId)
  }

  return normalizeFlatInboxStreamRows(raw)
}

function extractInboxStreamArray(payload) {
  if (Array.isArray(payload)) return payload
  if (payload && typeof payload === "object" && Array.isArray(payload.data)) {
    return payload.data
  }
  return []
}

function customStreamTextFields(typeId, streamName) {
  const value = pickText(streamName)
  if (!value) {
    return { name: "", search: "", hashtag: "", location: "" }
  }
  if (typeId === INBOX_STREAM_TYPE.IG_HASHTAG) {
    return { name: value, search: "", hashtag: value, location: "" }
  }
  if (typeId === INBOX_STREAM_TYPE.IG_LOCATION || typeId === INBOX_STREAM_TYPE.TW_LOCATION) {
    return { name: value, search: "", hashtag: "", location: value }
  }
  return { name: value, search: value, hashtag: "", location: "" }
}

/** GET inbox-stream/clients/{clientId} — streams with client subscriptions. */
export function normalizeInboxStreamFetchResponse(streams, clientId) {
  const clientKey = clientId != null ? String(clientId) : ""
  const rows = []

  for (const stream of streams || []) {
    if (!stream || typeof stream !== "object") continue

    const typeId = Number(stream.streamType?.id)
    if (!Number.isFinite(typeId)) continue

    const subscriptions = Array.isArray(stream.subscriptions) ? stream.subscriptions : []
    const clientSubs = clientKey
      ? subscriptions.filter((sub) => String(sub.client?.id) === clientKey)
      : subscriptions

    if (clientSubs.length === 0) continue

    const isCustom = isCustomStreamTypeId(typeId)
    const streamName = pickText(stream.streamName)

    if (isCustom && !streamName) continue

    const textFields = isCustom
      ? customStreamTextFields(typeId, streamName)
      : { name: "", search: "", hashtag: "", location: "" }

    for (const sub of clientSubs) {
      rows.push({
        typeId,
        flag: Boolean(sub.checked),
        streamName,
        ...textFields,
        streamId: stream.id ?? null,
        subscriptionId: sub.id ?? null,
        recordId: stream.id ?? null,
        isCustom,
      })
    }
  }

  return rows
}

function pickText(value) {
  return value != null ? String(value).trim() : ""
}

function normalizeFlatInboxStreamRows(raw) {
  return raw
    .map((row) => {
      const typeId = Number(row.streamType?.id)
      if (!Number.isFinite(typeId)) return null

      const isCustom = isCustomStreamTypeId(typeId)
      const streamName = pickText(row.streamName)
      const textFields = isCustom
        ? customStreamTextFields(typeId, streamName)
        : { name: "", search: "", hashtag: "", location: "" }

      const subscription = Array.isArray(row.subscriptions) ? row.subscriptions[0] : null

      return {
        typeId,
        flag: Boolean(subscription?.checked ?? row.checked),
        streamName,
        ...textFields,
        streamId: row.id ?? null,
        subscriptionId: subscription?.id ?? null,
        recordId: row.id ?? null,
        isCustom,
      }
    })
    .filter(Boolean)
}

export function getTypeIdForStream(stream, platformId) {
  const s = normalizeCustomStream(stream)
  if (s.kind === "fixed") {
    return FIXED_TYPE_ID_BY_PLATFORM[platformId]?.[s.id] ?? null
  }
  if (s.kind === "twitter_rule") {
    return TWITTER_RULE_TYPE_ID[s.streamType || "Hotel Name"] ?? null
  }
  if (s.kind === "hashtag") return CUSTOM_KIND_TYPE_ID.hashtag
  if (s.kind === "instagram_location") return CUSTOM_KIND_TYPE_ID.instagram_location
  return null
}

export function buildInboxStreamWriteBody({ flag, name, isChecked, search, hashtag, location }) {
  const body = {}
  const checked = isChecked ?? flag
  if (checked !== undefined) body.isChecked = Boolean(checked)

  const trimmed = name != null ? String(name).trim() : ""
  if (trimmed) body.search = trimmed

  const searchValue = search != null ? String(search).trim() : ""
  if (searchValue) body.search = searchValue

  const hashtagValue = hashtag != null ? normalizeHashtagValue(hashtag) : ""
  if (hashtagValue) body.hashtag = hashtagValue

  const locationValue = location != null ? String(location).trim() : ""
  if (locationValue) body.location = locationValue

  return body
}

/** POST body — always sends streamTypeId + isChecked; optional fields only when set. */
export function buildInboxStreamCreateBody({
  streamTypeId,
  typeId,
  isChecked,
  flag,
  search,
  hashtag,
  location,
}) {
  const body = {
    streamTypeId: Number(streamTypeId ?? typeId),
    isChecked: Boolean(isChecked ?? flag),
  }

  const searchValue = search != null ? String(search).trim() : ""
  if (searchValue) body.search = searchValue

  const hashtagValue = hashtag != null ? normalizeHashtagValue(hashtag) : ""
  if (hashtagValue) body.hashtag = hashtagValue

  const locationValue = location != null ? String(location).trim() : ""
  if (locationValue) body.location = locationValue

  return body
}

function streamTextValueForCreate(stream, textValue) {
  const s = normalizeCustomStream(stream)
  const value =
    textValue != null
      ? String(textValue).trim()
      : getCommittedSearchValue(s)
  if (!value) return {}

  if (s.kind === "hashtag") {
    const hashtag = normalizeHashtagValue(value)
    return hashtag ? { hashtag } : {}
  }

  if (
    s.kind === "instagram_location" ||
    (s.kind === "twitter_rule" && s.streamType === "Location")
  ) {
    return { location: value }
  }

  if (isCustomStream(s)) {
    return { search: value }
  }

  return {}
}

export function buildInboxStreamCreateBodyFromStream({
  stream,
  platformId,
  isChecked,
  flag,
  textValue,
}) {
  const normalized = normalizeCustomStream(stream)
  const streamTypeId = getTypeIdForStream(normalized, platformId)
  if (!streamTypeId) return null

  return buildInboxStreamCreateBody({
    streamTypeId,
    isChecked: isChecked ?? flag ?? normalized.enabled !== false,
    ...streamTextValueForCreate(normalized, textValue),
  })
}

/** PATCH body — always sends streamTypeId + isChecked; optional fields only when set. */
export function buildInboxStreamUpdateBody({
  streamTypeId,
  typeId,
  isChecked,
  flag,
  search,
  hashtag,
  location,
}) {
  const resolvedTypeId = Number(streamTypeId ?? typeId)
  const body = {
    streamTypeId: resolvedTypeId,
    isChecked: Boolean(isChecked ?? flag),
  }

  const searchValue = search != null ? String(search).trim() : ""
  if (searchValue) body.search = searchValue

  const hashtagValue = hashtag != null ? normalizeHashtagValue(hashtag) : ""
  if (hashtagValue) body.hashtag = hashtagValue

  const locationValue = location != null ? String(location).trim() : ""
  if (locationValue) body.location = locationValue

  return body
}

export function buildInboxStreamUpdateBodyFromStream({
  stream,
  platformId,
  isChecked,
  flag,
  textValue,
}) {
  const normalized = normalizeCustomStream(stream)
  const streamTypeId = normalized.typeId ?? getTypeIdForStream(normalized, platformId)
  if (!streamTypeId) return null

  return buildInboxStreamUpdateBody({
    streamTypeId,
    isChecked: isChecked ?? flag ?? normalized.enabled !== false,
    ...streamTextValueForCreate(normalized, textValue),
  })
}

function customStreamFromApiRow(typeId, row) {
  const textValue = pickText(row.streamName || row.name)
  if (!textValue) return null

  if (typeId === INBOX_STREAM_TYPE.IG_HASHTAG) {
    return {
      id: row.streamId != null ? String(row.streamId) : createStreamId(),
      kind: "hashtag",
      label: "Hashtag",
      enabled: row.flag,
      search: textValue,
      typeId,
      recordId: row.recordId,
      subscriptionId: row.subscriptionId,
      streamId: row.streamId,
    }
  }
  if (typeId === INBOX_STREAM_TYPE.IG_LOCATION) {
    return {
      id: row.streamId != null ? String(row.streamId) : createStreamId(),
      kind: "instagram_location",
      label: "Location",
      enabled: row.flag,
      search: textValue,
      typeId,
      recordId: row.recordId,
      subscriptionId: row.subscriptionId,
      streamId: row.streamId,
    }
  }
  if (
    typeId === INBOX_STREAM_TYPE.TW_HOTEL_NAME ||
    typeId === INBOX_STREAM_TYPE.TW_LOCATION ||
    typeId === INBOX_STREAM_TYPE.TW_SALES
  ) {
    const streamType =
      typeId === INBOX_STREAM_TYPE.TW_HOTEL_NAME
        ? "Hotel Name"
        : typeId === INBOX_STREAM_TYPE.TW_LOCATION
          ? "Location"
          : "Sales"
    return {
      id: row.streamId != null ? String(row.streamId) : createStreamId(),
      kind: "twitter_rule",
      label: streamType,
      streamType,
      enabled: row.flag,
      search: textValue,
      typeId,
      recordId: row.recordId,
      subscriptionId: row.subscriptionId,
      streamId: row.streamId,
    }
  }
  return null
}

/** PATCH inbox-stream/clients/{clientId}/{id} — `id` is the stream id from GET. */
export function resolveInboxStreamPatchId(stream, platformId, apiRows) {
  if (stream?.streamId != null && String(stream.streamId).trim() !== "") {
    return stream.streamId
  }
  if (stream?.recordId != null && String(stream.recordId).trim() !== "") {
    return stream.recordId
  }

  const resolvedTypeId =
    stream?.typeId ?? getTypeIdForStream(stream, platformId || inferPlatformFromStream(stream))
  if (!resolvedTypeId) return null

  const rows = Array.isArray(apiRows) ? apiRows : normalizeInboxStreamRows(apiRows)
  const name = String(stream?.search || stream?.streamName || "").trim()

  let hit = null
  if (stream?.subscriptionId != null) {
    hit = rows.find((r) => r.subscriptionId === stream.subscriptionId)
  }
  if (!hit && stream?.kind === "fixed") {
    hit = rows.find((r) => r.typeId === resolvedTypeId && !isCustomStreamTypeId(r.typeId))
  }
  if (!hit && name) {
    hit = rows.find(
      (r) =>
        r.typeId === resolvedTypeId &&
        String(r.streamName || r.name || "").trim() === name,
    )
  }
  if (!hit) {
    hit = rows.find((r) => r.typeId === resolvedTypeId)
  }

  return hit?.streamId ?? hit?.recordId ?? null
}

/** Merge GET rows into UI settings shape used by Streams.jsx */
export function inboxStreamRowsToSettings(rows, connectedPlatformIds) {
  const settings = createDefaultStreamSettings()
  const connected =
    connectedPlatformIds instanceof Set
      ? connectedPlatformIds
      : new Set(connectedPlatformIds || [])
  const grouped = new Map()

  for (const row of normalizeInboxStreamRows(rows)) {
    const platformId = TYPE_ID_PLATFORM[row.typeId]
    if (!platformId) continue
    if (connected.size > 0 && !connected.has(platformId)) continue
    if (!grouped.has(platformId)) grouped.set(platformId, [])
    grouped.get(platformId).push(row)
  }

  for (const [platformId, platformRows] of grouped.entries()) {
    const fixedDefaults = FIXED_STREAM_DEFAULTS[platformId] || []
    const fixed = fixedDefaults.map((def) => {
      const typeId = FIXED_TYPE_ID_BY_PLATFORM[platformId]?.[def.id]
      const hit = platformRows.find(
        (r) => r.typeId === typeId && !isCustomStreamTypeId(r.typeId),
      )
      if (!hit) return { ...def }
      return {
        ...def,
        enabled: hit.flag,
        typeId: hit.typeId,
        recordId: hit.recordId,
        subscriptionId: hit.subscriptionId,
        streamId: hit.streamId,
      }
    })

    const custom = []
    for (const row of platformRows) {
      if (!isCustomStreamTypeId(row.typeId)) continue
      const stream = customStreamFromApiRow(row.typeId, row)
      if (stream) custom.push(stream)
    }

    settings[platformId] = { streams: [...fixed, ...custom] }
  }

  return settings
}

export function streamExistsOnServer(stream, platformId, apiRows) {
  const resolvedTypeId =
    stream.typeId ?? getTypeIdForStream(stream, platformId || inferPlatformFromStream(stream))
  if (!resolvedTypeId) return false

  const rows = Array.isArray(apiRows) ? apiRows : normalizeInboxStreamRows(apiRows)

  if (stream.streamId != null) {
    return rows.some((r) => r.streamId === stream.streamId)
  }

  if (stream.subscriptionId != null) {
    return rows.some((r) => r.subscriptionId === stream.subscriptionId)
  }

  if (stream.recordId != null) {
    return rows.some((r) => r.recordId === stream.recordId)
  }

  const name = String(stream.search || "").trim()

  if (stream.kind === "fixed") {
    return rows.some((r) => r.typeId === resolvedTypeId)
  }

  if (!name) return false

  return rows.some(
    (r) => r.typeId === resolvedTypeId && String(r.name || "").trim() === name,
  )
}

function inferPlatformFromStream(stream) {
  const typeId = stream.typeId ?? getTypeIdForStream(stream, "facebook")
  if (stream.kind === "fixed") {
    for (const [platformId, map] of Object.entries(FIXED_TYPE_ID_BY_PLATFORM)) {
      if (Object.values(map).includes(typeId)) return platformId
    }
  }
  if (stream.kind === "twitter_rule") return "twitter"
  if (stream.kind === "hashtag" || stream.kind === "instagram_location") return "instagram"
  return "facebook"
}
